import { useState, useEffect, useRef, FormEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { resetPassword } from '../api/auth';
import { validatePassword } from '../utils/validation';
import { Spinner } from '../components/Spinner';

const REDIRECT_SECONDS = 5;

type PageState = 'form' | 'submitting' | 'success' | 'error';

/**
 * Password reset landing page for the emailed link (`/reset-password?token=...`).
 *
 * Deliberately does NOT call the backend automatically on page load — `POST
 * /api/auth/reset-password` only fires on an explicit form submission, once the user has typed a
 * new password. This mirrors `VerifyEmail.tsx`'s anti-prefetch rationale (see SF-102 spec,
 * Component 5/9): a mail-security scanner following the link on the server's behalf only ever
 * touches inert HTML, since it has no password to submit.
 */
export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [state, setState] = useState<PageState>(token ? 'form' : 'error');
  const [errorMessage, setErrorMessage] = useState(
    token ? '' : 'This password reset link is missing a token.'
  );
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [formError, setFormError] = useState('');

  const redirectTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clear the countdown/redirect timer on unmount so it can't fire after navigating away.
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearInterval(redirectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (state !== 'success') {
      return;
    }
    redirectTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (redirectTimerRef.current) {
            clearInterval(redirectTimerRef.current);
          }
          navigate('/?login=true');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (redirectTimerRef.current) {
        clearInterval(redirectTimerRef.current);
      }
    };
  }, [state, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!token || state === 'submitting') {
      return;
    }

    if (!validatePassword(newPassword)) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setState('submitting');

    const { errorCode, errorMessage: resetErrorMessage } = await resetPassword(token, newPassword, confirmNewPassword);
    if (errorCode || resetErrorMessage) {
      if (errorCode === 'INVALID_PASSWORD_RESET_TOKEN') {
        setErrorMessage('This password reset link is invalid or has expired.');
        setState('error');
        return;
      }
      setFormError(resetErrorMessage ?? 'An error occurred');
      setState('form');
      return;
    }

    setState('success');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: '#110f0d' }}
    >
      <div
        className="relative w-full max-w-[420px]"
        style={{
          background: '#1c1916',
          border: '1px solid #2e2924',
          borderRadius: 8,
          padding: '36px 32px',
        }}
      >
        <div
          className="absolute top-[-1px] left-6 right-6 h-[2px]"
          style={{ background: 'linear-gradient(90deg, transparent, #6a9e8e, transparent)' }}
        />

        <h1
          className="text-center mb-6"
          style={{ fontFamily: "'Cinzel', serif", fontSize: 20, fontWeight: 600, color: '#f0e8dc' }}
        >
          Reset Your Password
        </h1>

        {(state === 'form' || state === 'submitting') && (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="reset-new-password"
                className="block mb-1.5 uppercase tracking-wide"
                style={{ fontFamily: "'Cinzel', serif", fontSize: 12, fontWeight: 500, color: '#b5a99c' }}
              >
                New Password
              </label>
              <input
                id="reset-new-password"
                type="password"
                placeholder="Enter your new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={state === 'submitting'}
                className="w-full px-4 py-3 rounded focus:outline-none transition-colors"
                style={{
                  background: '#0a0908',
                  border: '1px solid #2e2924',
                  color: '#f0e8dc',
                  fontSize: 16,
                }}
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="reset-confirm-password"
                className="block mb-1.5 uppercase tracking-wide"
                style={{ fontFamily: "'Cinzel', serif", fontSize: 12, fontWeight: 500, color: '#b5a99c' }}
              >
                Confirm New Password
              </label>
              <input
                id="reset-confirm-password"
                type="password"
                placeholder="Confirm your new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                disabled={state === 'submitting'}
                className="w-full px-4 py-3 rounded focus:outline-none transition-colors"
                style={{
                  background: '#0a0908',
                  border: '1px solid #2e2924',
                  color: '#f0e8dc',
                  fontSize: 16,
                }}
              />
            </div>

            {formError && (
              <div className="mb-4 px-3 py-2 rounded text-sm" style={{ background: '#2a1414', border: '1px solid #4a2020', color: '#ff5555' }}>
                {formError}
              </div>
            )}

            <button
              type="submit"
              disabled={state === 'submitting'}
              className="w-full py-3 rounded font-semibold transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #2a4a4a, #1e3a3a)',
                color: '#fff',
                fontFamily: "'Cinzel', serif",
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '0.06em',
                border: '1px solid rgba(106,158,142, 0.3)',
                cursor: state === 'submitting' ? 'not-allowed' : 'pointer',
              }}
            >
              {state === 'submitting' ? (
                <>
                  <Spinner size="sm" /> Resetting...
                </>
              ) : (
                'Reset password'
              )}
            </button>
          </form>
        )}

        {state === 'success' && (
          <div className="text-center">
            <p className="mb-4" style={{ color: '#f0e8dc', fontSize: 15 }}>
              Password reset — redirecting to login in {countdown}…
            </p>
            <Link
              to="/?login=true"
              className="hover:underline"
              style={{ color: '#6a9e8e', fontSize: 14 }}
            >
              Log in now
            </Link>
          </div>
        )}

        {state === 'error' && (
          <div>
            <div
              className="mb-5 px-3 py-2 rounded text-sm text-center"
              style={{ background: '#2a1414', border: '1px solid #4a2020', color: '#ff5555' }}
            >
              {errorMessage}
            </div>

            <p className="text-center" style={{ color: '#b5a99c', fontSize: 14 }}>
              <Link
                to="/?login=true"
                className="hover:underline"
                style={{ color: '#6a9e8e' }}
              >
                Return to login
              </Link>{' '}
              to request a new reset link.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
